/**
 * SessionExpiryHandler - Monitors for auth errors (observability only)
 *
 * Better Auth manages its own session lifecycle — when a BA session expires,
 * useSession() returns null, AuthContext clears the session, and ProtectedRoutes
 * redirects to login. No forced sign-out is needed here.
 *
 * This component logs UNAUTHORIZED errors from data-layer queries (e.g.,
 * getAnalysisHistory called with an expired or invalid BA JWT) for observability.
 * It does NOT sign the user out — doing so would create a login loop with BA's
 * own token-refresh cycle, and for anon BA users it would destroy the anon
 * session instead of letting BA reissue a JWT cleanly.
 *
 * @since 2025-12-01 (P1: Multi-model consensus recommendation)
 * @modified 2026-03-29 — Disabled forced sign-out to fix BA login loop (PR #657)
 * @modified 2026-04-23 — Updated comment: Supabase decommissioned; BA is the sole auth provider
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isApiError } from '@dramadetector/api-client';
import { useAuth } from '../contexts/AuthContext';
import { captureEvent } from '../lib/analytics';

export function SessionExpiryHandler() {
  const queryClient = useQueryClient();
  const { accessToken } = useAuth();

  useEffect(() => {
    if (!accessToken) return;

    const queryCache = queryClient.getQueryCache();

    const unsubscribe = queryCache.subscribe((event) => {
      if (event.type !== 'updated') return;
      if (event.action.type !== 'error') return;

      const error = event.action.error;

      if (isApiError(error) && error.code === 'UNAUTHORIZED') {
        captureEvent('unauthorized_query_error', {
          source: 'SessionExpiryHandler',
          message: error.message,
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [accessToken, queryClient]);

  return null;
}
